import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StateCreator } from 'zustand';
import { SessionData } from '../types';

// Interface for the Zustand store
interface WatchlistStore {
  // State
  savedSessions: SessionData[];
  visibleSessionIds: string[];
  activeSessionId: string | null;
  maxSessions: number;
  lastCleanupTime: Date;

  // Actions for session management
  addSession: (session: SessionData) => void;
  removeSession: (sessionId: string) => void;
  updateSession: (sessionId: string, updates: Partial<SessionData>) => void;
  setActiveSession: (sessionId: string | null) => void;
  toggleSessionVisibility: (sessionId: string) => void;
  showAllSessions: () => void;
  hideAllSessions: () => void;

  // Memory management actions
  performCleanup: () => void;
  clearOldSessions: () => void;
  getMemoryUsage: () => number;

  // Utility actions
  getSessionById: (sessionId: string) => SessionData | undefined;
  getActiveSessions: () => SessionData[];
  getSortedSessions: () => SessionData[];
}

// Memory management constants
const MAX_SESSIONS = 10;
const CLEANUP_INTERVAL_HOURS = 24;
const MAX_MEMORY_LIMIT_MB = 50; // Estimated memory limit for sessions
const SYDNEY_CLEANUP_HOUR = 3; // 3 AM Sydney time

// Memory efficient session management functions
const calculateSessionSize = (session: SessionData): number => {
  // Rough estimation of session memory usage in bytes
  const baseSize = 1000; // Base object overhead
  const resultsSize = session.results.length * 500; // Estimate per result
  const historySize =
    (session.evHistory.length +
      session.liquidityHistory.length +
      session.backOddsHistory.length) *
    100; // Estimate per history entry

  return baseSize + resultsSize + historySize;
};

// Helper function to determine if cleanup should run now
const shouldCleanupNow = (lastCleanup: Date): boolean => {
  const now = new Date();
  const timeSinceCleanup = now.getTime() - lastCleanup.getTime();
  const hoursThreshold = CLEANUP_INTERVAL_HOURS * 60 * 60 * 1000;

  // Check if it's past 3 AM Sydney time today and we haven't cleaned up today
  const sydneyTime = new Date(
    now.toLocaleString('en-US', { timeZone: 'Australia/Sydney' })
  );
  const today3AM = new Date(sydneyTime);
  today3AM.setHours(SYDNEY_CLEANUP_HOUR, 0, 0, 0);

  const lastCleanupSydney = new Date(
    lastCleanup.toLocaleString('en-US', { timeZone: 'Australia/Sydney' })
  );
  const isAfter3AM = sydneyTime >= today3AM;
  const lastCleanupWasBeforeToday3AM = lastCleanupSydney < today3AM;

  return (
    timeSinceCleanup > hoursThreshold ||
    (isAfter3AM && lastCleanupWasBeforeToday3AM)
  );
};

// Type the state creator properly
const createWatchlistStore: StateCreator<WatchlistStore> = (set, get) => ({
  savedSessions: [],
  visibleSessionIds: [],
  activeSessionId: null,
  maxSessions: MAX_SESSIONS,
  lastCleanupTime: new Date(),

  addSession: (session: SessionData) => {
    set((state) => {
      let newSessions = [...state.savedSessions];

      // Remove existing session with same ID if it exists
      newSessions = newSessions.filter((s) => s.id !== session.id);

      // Add new session
      newSessions.unshift(session);

      // Enforce memory limits
      if (newSessions.length > MAX_SESSIONS) {
        // Remove oldest sessions
        newSessions = newSessions.slice(0, MAX_SESSIONS);
      }

      // Check memory usage and cleanup if needed
      let totalSize = newSessions.reduce(
        (acc, s) => acc + calculateSessionSize(s),
        0
      );
      const maxSizeBytes = MAX_MEMORY_LIMIT_MB * 1024 * 1024;

      if (totalSize > maxSizeBytes) {
        // Remove sessions with largest memory footprint first
        newSessions.sort(
          (a, b) => calculateSessionSize(b) - calculateSessionSize(a)
        );

        while (totalSize > maxSizeBytes && newSessions.length > 1) {
          const removed = newSessions.pop();
          if (removed) {
            totalSize -= calculateSessionSize(removed);
          }
        }

        // Re-sort by last update time (newest first)
        newSessions.sort(
          (a, b) =>
            new Date(b.lastUpdateTime).getTime() -
            new Date(a.lastUpdateTime).getTime()
        );
      }

      return {
        savedSessions: newSessions,
        visibleSessionIds: state.visibleSessionIds.filter((id) =>
          newSessions.some((s) => s.id === id)
        ),
      };
    });
  },

  removeSession: (sessionId: string) => {
    set((state) => ({
      savedSessions: state.savedSessions.filter((s) => s.id !== sessionId),
      visibleSessionIds: state.visibleSessionIds.filter(
        (id) => id !== sessionId
      ),
      activeSessionId:
        state.activeSessionId === sessionId ? null : state.activeSessionId,
    }));
  },

  updateSession: (sessionId: string, updates: Partial<SessionData>) => {
    set((state) => ({
      savedSessions: state.savedSessions.map((session) =>
        session.id === sessionId
          ? { ...session, ...updates, lastUpdateTime: new Date() }
          : session
      ),
    }));
  },

  setActiveSession: (sessionId: string | null) => {
    set({ activeSessionId: sessionId });
  },

  toggleSessionVisibility: (sessionId: string) => {
    set((state) => {
      const isVisible = state.visibleSessionIds.includes(sessionId);
      return {
        visibleSessionIds: isVisible
          ? state.visibleSessionIds.filter((id) => id !== sessionId)
          : [...state.visibleSessionIds, sessionId],
      };
    });
  },

  showAllSessions: () => {
    set((state) => ({
      visibleSessionIds: state.savedSessions.map((s) => s.id),
    }));
  },

  hideAllSessions: () => {
    set({ visibleSessionIds: [] });
  },

  performCleanup: () => {
    const state = get();

    if (shouldCleanupNow(state.lastCleanupTime)) {
      // Remove sessions older than 7 days
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      set((currentState) => {
        const activeSessions = currentState.savedSessions.filter(
          (session) => new Date(session.lastUpdateTime) > weekAgo
        );

        return {
          savedSessions: activeSessions,
          visibleSessionIds: currentState.visibleSessionIds.filter((id) =>
            activeSessions.some((s) => s.id === id)
          ),
          activeSessionId: activeSessions.some(
            (s) => s.id === currentState.activeSessionId
          )
            ? currentState.activeSessionId
            : null,
          lastCleanupTime: new Date(),
        };
      });

      console.log(
        '[WatchlistStore] Auto-cleanup completed at',
        new Date().toISOString()
      );
    }
  },

  clearOldSessions: () => {
    set((state) => {
      const dayAgo = new Date();
      dayAgo.setDate(dayAgo.getDate() - 1);

      const recentSessions = state.savedSessions.filter(
        (session) => new Date(session.lastUpdateTime) > dayAgo
      );

      return {
        savedSessions: recentSessions,
        visibleSessionIds: state.visibleSessionIds.filter((id) =>
          recentSessions.some((s) => s.id === id)
        ),
        activeSessionId: recentSessions.some(
          (s) => s.id === state.activeSessionId
        )
          ? state.activeSessionId
          : null,
      };
    });
  },

  getMemoryUsage: () => {
    const state = get();
    return (
      state.savedSessions.reduce(
        (acc, session) => acc + calculateSessionSize(session),
        0
      ) /
      (1024 * 1024)
    ); // Return in MB
  },

  getSessionById: (sessionId: string) => {
    const state = get();
    return state.savedSessions.find((s) => s.id === sessionId);
  },

  getActiveSessions: () => {
    const state = get();
    return state.savedSessions.filter((session) =>
      state.visibleSessionIds.includes(session.id)
    );
  },

  getSortedSessions: () => {
    const state = get();
    return [...state.savedSessions].sort(
      (a, b) =>
        new Date(b.lastUpdateTime).getTime() -
        new Date(a.lastUpdateTime).getTime()
    );
  },
});

// Create the store with persistence
export const useWatchlistStore = create<WatchlistStore>()(
  persist(createWatchlistStore, {
    name: 'gamechanger-watchlist',
    version: 1,
    // Only persist essential data, not computed values
    partialize: (state) => ({
      savedSessions: state.savedSessions,
      visibleSessionIds: state.visibleSessionIds,
      activeSessionId: state.activeSessionId,
      lastCleanupTime: state.lastCleanupTime,
    }),
    // Migration function for future versions
    migrate: (persistedState: any, version: number) => {
      if (version === 0) {
        // Migration from version 0 to 1
        return {
          ...persistedState,
          lastCleanupTime: new Date(),
        };
      }
      return persistedState;
    },
    // Storage configuration with Date handling
    storage: {
      getItem: (name) => {
        try {
          const item = localStorage.getItem(name);
          if (!item) return null;

          const parsed = JSON.parse(item);

          // Convert date strings back to Date objects
          if (parsed?.state) {
            if (parsed.state.lastCleanupTime) {
              parsed.state.lastCleanupTime = new Date(
                parsed.state.lastCleanupTime
              );
            }
            if (parsed.state.savedSessions) {
              parsed.state.savedSessions = parsed.state.savedSessions.map(
                (session: any) => ({
                  ...session,
                  lastUpdateTime: new Date(session.lastUpdateTime),
                })
              );
            }
          }

          return parsed;
        } catch (error) {
          console.error(
            '[WatchlistStore] Error reading from localStorage:',
            error
          );
          return null;
        }
      },
      setItem: (name, value) => {
        try {
          localStorage.setItem(name, JSON.stringify(value));
        } catch (error: any) {
          console.error(
            '[WatchlistStore] Error writing to localStorage:',
            error
          );
          // If storage is full, try to clear old data
          if (error.name === 'QuotaExceededError') {
            try {
              // Clear this specific item and try again
              localStorage.removeItem(name);
              localStorage.setItem(name, JSON.stringify(value));
            } catch (retryError: any) {
              console.error(
                '[WatchlistStore] Failed to clear and retry localStorage:',
                retryError
              );
            }
          }
        }
      },
      removeItem: (name) => {
        try {
          localStorage.removeItem(name);
        } catch (error) {
          console.error(
            '[WatchlistStore] Error removing from localStorage:',
            error
          );
        }
      },
    },
  })
);

// Hook for automatic cleanup - call this in your main App component
export const useWatchlistCleanup = () => {
  const performCleanup = useWatchlistStore((state) => state.performCleanup);

  React.useEffect(() => {
    // Run cleanup on mount
    performCleanup();

    // Set up interval for regular cleanup checks
    const interval = setInterval(performCleanup, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, [performCleanup]);
};
